import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageSizeTypeEnum, ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import type { IProduct, IProductImage, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Image } from '@inter-ikea-kompis/component-image';
import { ImageAspectRatioEnum } from '@inter-ikea-kompis/component-image';
import { MediaCarousel } from '@inter-ikea-kompis/component-media-carousel';

import ProductCardMediaEnum from '../../enums/ProductCardMediaEnum.js';
import type IProductCardLocalizedInformation from '../../types/IProductCardLocalizedInformation.js';
import type IProductCardTranslations from '../../types/IProductCardTranslations.js';
import ProductCardMediaStyle from './ProductCardMediaStyle.js';

/**
 * Product Card component.
 *
 * @event carouselChange Triggered when a carousel slide should be changed.
 * @event carouselShowAllThumbnails Triggered when the show all thumbnails button is clicked in the carousel.
 */

export default class ProductCardMedia extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Product object retrieved from ProductService.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Sets how media should be displayed.
	@property({ type: String })
	public media: ProductCardMediaEnum = ProductCardMediaEnum.singleImage;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: IProductCardLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductCardTranslations | null = null;

	// Selected index of the carousel slides.
	@property({ type: Number })
	public carouselSelectedIndex = 0;

	// Max limit of thumbnails to be displayed in the carousel.
	@property({ type: Number })
	public carouselThumbnailLimit: number | null = null;

	// Shows all thumbnails in the carousel when a limit has been set.
	@property({ type: Boolean })
	public carouselShowAll = false;

	// Show scrollbar representing pageIndex
	@property({ type: Boolean })
	public carouselShowScrollbar = false;

	// Show arrows in carousel.
	@property({ type: Boolean })
	public carouselShowArrows = false;

	// "true" if hovered.
	@property({ type: Boolean })
	public hovered = false;

	// The aspect ratio that the images should adopt.
	@property({ type: String })
	public imageAspectRatio?: ImageAspectRatioEnum = ImageAspectRatioEnum.ORIGINAL;

	// Defines the image sort order. When only one image is being displayed, the first image in the list is used.
	@property({ type: Object })
	public imageSortOrder?: ImageTypeNameEnum[] | null = null;

	// Defines the image sort order for the image displayed on hover when "media" is set to "switchImageOnHover".
	@property({ type: Object })
	public hoverImageSortOrder?: ImageTypeNameEnum[] = [
		ImageTypeNameEnum.functionalProductPicture,
		ImageTypeNameEnum.contextProductPicture
	];

	/**
	 * Render.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		if (!this.product?.content) {
			return null;
		}

		return html`
			<style>
				${ProductCardMediaStyle.getStyle({
					theme: this.theme,
					isHovered: this.hovered,
					hasHoverImage: !!this.getHoverImage()
				})}
			</style>
			${this.getContainer()}
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-image', Image);
		this.customElements.define('kompis-media-carousel', MediaCarousel);

		super.connectedCallback();
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation;
	}

	private getTranslations(): IProductCardTranslations {
		return this.translations
			? this.translations
			: <IProductCardTranslations>this.localizedInformation?.translations;
	}
	/**
	 * Returns container.
	 *
	 * @returns Template.
	 */
	private getContainer(): RenderResult {
		return html` <div class="container">${this.getMedia()}</div> `;
	}

	/**
	 * Returns media.
	 *
	 * @returns Template.
	 */
	private getMedia(): RenderResult {
		switch (this.media) {
			case ProductCardMediaEnum.singleImage:
			case ProductCardMediaEnum.switchImageOnHover:
				return this.getImage();
			case ProductCardMediaEnum.carousel:
				return this.getCarousel();
			default:
				return null;
		}
	}

	/**
	 * Returns single image.
	 *
	 * @returns Template.
	 */
	private getImage(): RenderResult {
		if (!this.product?.content?.image?.length) {
			return null;
		}

		const productImage =
			this.getFirstImageMatchingType(this.product.content.image, this.imageSortOrder ?? null) ||
			this.product.content.image.find((image) => image.size === ImageSizeTypeEnum.S5) ||
			null;

		if (!productImage) {
			return null;
		}

		return html`
			<div class="imageContainer">
				<div class="singleImage">
					<kompis-image
						.src="${productImage.url}"
						.productImage="${true}"
						.aspectRatio="${this.imageAspectRatio}"
					></kompis-image>
				</div>
				${this.getHoverImage()}
			</div>
		`;
	}

	/**
	 * Returns hover image.
	 *
	 * @returns Template.
	 */
	private getHoverImage(): RenderResult {
		if (this.media !== ProductCardMediaEnum.switchImageOnHover) {
			return null;
		}

		const hoverImage = this.getFirstImageMatchingType(
			this.product.content?.image ?? null,
			this.hoverImageSortOrder ?? null
		);

		if (!hoverImage) {
			return null;
		}

		return html`
			<div class="hoverImage">
				<kompis-image
					.src="${hoverImage.url}"
					.productImage="${true}"
					.aspectRatio="${this.imageAspectRatio}"
				></kompis-image>
			</div>
		`;
	}

	/**
	 * Returns carousel.
	 *
	 * @returns Template.
	 */
	private getCarousel(): RenderResult {
		if (!this.product?.content?.image) {
			return null;
		}

		const images = this.getImagesSortedByType(
			this.product.content.image,
			this.imageSortOrder ?? null
		);
		const mediaItems = images.map((image) => ({
			src: image.url,
			alt: this.product?.content?.name,
			productImage: true
		}));

		if (mediaItems.length === 0) {
			return null;
		}

		return html`
			<div class="carousel">
				<kompis-media-carousel
					@change="${(event: CustomEvent) =>
						this.dispatchEvent(
							new CustomEvent('carouselChange', {
								bubbles: true,
								detail: {
									carouselSelectedIndex: event.detail.selectedIndex,
									type: event.detail.type
								}
							})
						)}"
					@showAllThumbnails="${(event: CustomEvent) =>
						this.dispatchEvent(
							new CustomEvent('carouselShowAllThumbnails', {
								bubbles: true,
								detail: { carouselShowAll: event.detail.showAll }
							})
						)}"
					.theme="${this.theme}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.skipListingLabel="${this.getTranslations().carouselSkipListing}"
					.showLessLabel="${this.getTranslations().carouselShowLess}"
					.mediaItems="${mediaItems}"
					.selectedIndex="${this.carouselSelectedIndex}"
					.thumbnailLimit="${this.carouselThumbnailLimit}"
					.showAll="${this.carouselShowAll}"
					.showScrollbar="${this.carouselShowScrollbar}"
					.showArrows="${this.carouselShowArrows}"
					.aspectRatio="${this.imageAspectRatio}"
				></kompis-media-carousel>
			</div>
		`;
	}

	/**
	 * Returns images sorted by type.
	 *
	 * @param images Images.
	 * @param types List of types to sort by in priority order.
	 * @returns Sorted images.
	 */
	private getImagesSortedByType(
		images: IProductImage[],
		types: ImageTypeNameEnum[] | null
	): IProductImage[] {
		if (!types) {
			return images.filter((image) => image.size === ImageSizeTypeEnum.S5);
		}

		const result: IProductImage[] = [];

		// Add images to start of result based on types order.
		for (const type of types) {
			for (const image of images) {
				if (image.size === ImageSizeTypeEnum.S5 && image.typeName == type) {
					result.push(image);
				}
			}
		}

		// Add the rest of the images.
		for (const image of images) {
			if (image.size === ImageSizeTypeEnum.S5 && !types.includes(image.typeName)) {
				result.push(image);
			}
		}

		return result;
	}

	/**
	 * Returns first image matching the type.
	 *
	 * @param images Images.
	 * @param types List of types to sort by in priority order.
	 * @returns First image matching the type.
	 */
	private getFirstImageMatchingType(
		images: IProductImage[] | null,
		types: ImageTypeNameEnum[] | null
	): IProductImage | null {
		if (!types || !images || !images.length) {
			return null;
		}

		for (const type of types) {
			for (const image of images) {
				if (image.size === ImageSizeTypeEnum.S5 && image.typeName == type) {
					return image;
				}
			}
		}

		return null;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-product-card-media': ProductCardMedia;
	}
}
